import { Cake, CakeActions, CakeContent } from '@campfire/cake';
import { CircularProgressOverlay } from '@campfire/circular-progress-overlay';
import { TabletButton } from '@campfire/tablet-button';
import { Box, List, ListItem, ListItemIcon, ListItemText, Theme, Typography } from '@material-ui/core';
import { ChevronRight, EmojiPeople, Event, EventAvailable, SendRounded, AccessTime } from '@material-ui/icons';
import { createStyles, makeStyles } from '@material-ui/styles';
import pluralize from 'pluralize';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { arrayHead } from '../../common/functions/array-head';
import { TaskCompleteIcon } from '../../common/icons/TaskCompleteIcon';
import { useSession } from '../auth/useSession';
import { useUser } from '../auth/useUser';
import { useOrgInfo } from '../auth/use-org-info/use-org-info';
import { useSnackbar } from '../config/useSnackbar';
import { recordInteraction } from '../interactions/recordInteraction';
import { useCampfireQuery } from '../network/useCampfireQuery';
import { useEndpointFetch } from '../network/useEndpointFetch';
import { GET_ALL_APPLICANT_TASKS } from './model/applicant-shell-query.gql';
import { GET_USER_INTERACTIONS } from './model/user-interactions-query.gql';
import {
  GetAllApplicantTasks,
  GetAllApplicantTasksVariables,
  GetAllApplicantTasks_vm_profile_completedTasks_refereeContacts as RefereeContact,
} from './model/__generated__/GetAllApplicantTasks';
import { GetUserInteractions, GetUserInteractionsVariables } from './model/__generated__/GetUserInteractions';
import { SUYPOverviewTasksListTask } from './model/__generated__/SUYPOverviewTasksListTask';
import {
  GET_APPLICATION_APPLY_ROLES,
  GET_APPLICATION_AVAILABILITY_SIMPLE,
  GET_APPLICATION_ENROL_ACTIVITIES,
  GET_APPLICATION_AUTO_VERIFICATION,
} from '../../screens/admin/admin-console/admin-console-content-pages/admin-console-onboarding-applications/admin-console-applicant-screening.gql';
import { GetApplicationAvailabilitySimple } from '../../screens/admin/admin-console/admin-console-content-pages/admin-console-onboarding-applications/__generated__/GetApplicationAvailabilitySimple';
import { GetApplicationEnrolActivities } from '../../screens/admin/admin-console/admin-console-content-pages/admin-console-onboarding-applications/__generated__/GetApplicationEnrolActivities';
import { GetApplicationApplyRoles } from '../../screens/admin/admin-console/admin-console-content-pages/admin-console-onboarding-applications/__generated__/GetApplicationApplyRoles';
import { GetApplicationAutoVerification } from '../../screens/admin/admin-console/admin-console-content-pages/admin-console-onboarding-applications/__generated__/GetApplicationAutoVerification';
import { GET_ONBOARDING_OPTION_STAGES } from '../../common/onboarding-option-task/onboarding-option-stages-model.gql';
import { GetOnboardingOptionStages } from '../../common/onboarding-option-task/__generated__/GetOnboardingOptionStages';

const CONTRACT_SUBMISSION_TOKEN_RETRY_LIMIT = 30;
const CONTRACT_SUBMISSION_TOKEN_REFRESH_INTERVAL = 5000;

type ApplicantTaskListItem = {
  task: SUYPOverviewTasksListTask;
  completedDate?: string;
  status?: string;
  refereeContacts?: RefereeContact[];
};

const isTaskApproved = (status?: string) => status === 'approved' || status === 'admin-approved';

interface Props {
  progress: number;
  setProgress: (number: number, handleSuccess?: () => void) => void;
}

const SUYPOverview = (props: Props) => {
  const { progress, setProgress } = props;
  const { reloadSession } = useSession();
  const { user } = useUser();
  const { userId } = user;
  const orgInfo = useOrgInfo();
  const programLabel = orgInfo?.menuLabels?.wordProgram || 'Program';
  const activityLabel = orgInfo?.menuLabels?.wordActivity || 'Activity';
  const { postInteraction } = recordInteraction('vm');
  const { setSnackbar } = useSnackbar();

  const completeVolunteerProfile = useEndpointFetch<{ profileId: string }>('/vm/volunteer/profile/complete');

  const allApplicantTasksQuery = useCampfireQuery<GetAllApplicantTasks, GetAllApplicantTasksVariables>(
    GET_ALL_APPLICANT_TASKS,
    {
      options: {
        variables: {
          userId,
        },
      },
    }
  );
  const { data: interactionsResponse, loading: interactionsLoading } = useCampfireQuery<
    GetUserInteractions,
    GetUserInteractionsVariables
  >(GET_USER_INTERACTIONS, {
    options: {
      variables: {
        page: 'suyp',
        action: 'viewed',
      },
    },
  });

  const [applicationAvailabilitySimple, setApplicationAvailabilitySimple] = React.useState(false);
  const [applicationActivityEnrolment, setApplicationActivityEnrolment] = React.useState(false);
  const [applicationApplyRoles, setApplicationApplyRoles] = React.useState(false);

  const { data: availabilitySimple } = useCampfireQuery<GetApplicationAvailabilitySimple, undefined>(
    GET_APPLICATION_AVAILABILITY_SIMPLE
  );

  const { data: applicationActivity } = useCampfireQuery<GetApplicationEnrolActivities, undefined>(
    GET_APPLICATION_ENROL_ACTIVITIES
  );

  const { data: applyRoles } = useCampfireQuery<GetApplicationApplyRoles, undefined>(GET_APPLICATION_APPLY_ROLES);

  const { data: autoVerification } = useCampfireQuery<GetApplicationAutoVerification, undefined>(
    GET_APPLICATION_AUTO_VERIFICATION
  );

  const { data: onboardingOptionStages } = useCampfireQuery<GetOnboardingOptionStages, {}>(
    GET_ONBOARDING_OPTION_STAGES
  );

  // Track auto verification setting - defaults to false (manual approval required)
  const applicationAutoVerification = autoVerification?.vm.applicationAutoVerification ?? false;

  const funnel = allApplicantTasksQuery.data?.vm.application?.funnel;
  const funnelHasRoles = (funnel?.activityRoles?.length ?? 0) > 0;
  const funnelHasActivities = (funnel?.activities?.length ?? 0) > 0;

  useEffect(() => {
    setApplicationAvailabilitySimple(availabilitySimple?.vm.configItem?.configItemValue?.flagValue ?? false);
    setApplicationActivityEnrolment(applicationActivity?.vm.configItem?.configItemValue?.flagValue ?? false);
    setApplicationApplyRoles(applyRoles?.vm.configItem?.configItemValue?.flagValue ?? false);
  }, [availabilitySimple, applicationActivity, applyRoles]);

  useEffect(() => {
    if (interactionsResponse && !interactionsResponse.vm.hasUserInteraction) postInteraction('suyp', 'viewed');
  }, [interactionsResponse]);

  // Helper function to get stage assignment for an onboarding option
  const getStageAssignment = (optionType: string) => {
    return (
      onboardingOptionStages?.vm.onboardingOptionStages.find((stage) => stage.optionType === optionType)?.taskStage ||
      null
    );
  };

  const getOptionContent = (optionType: string) => {
    const stage = onboardingOptionStages?.vm.onboardingOptionStages.find((s) => s.optionType === optionType);
    return { title: stage?.customTitle || null, description: stage?.customDescription || null };
  };

  // Helper function to check if an onboarding option should be shown in Additional section
  const shouldShowOnboardingOption = (optionType: string) => {
    if (!allApplicantTasksQuery.data) {
      return false;
    }

    const stageAssignment = getStageAssignment(optionType);

    // If not assigned to a stage, always show (legacy behavior)
    if (!stageAssignment) {
      return true;
    }

    // If assigned to a stage, use stage progression logic
    const { application } = allApplicantTasksQuery.data.vm;
    const currentStageId = application?.currentStageId;
    const completedStageIds = application?.completedStageIds || [];

    // If no current stage set, only show unstaged items
    if (!currentStageId) {
      return false;
    }

    // Show options from completed stages
    if (completedStageIds.includes(stageAssignment.taskStageId)) {
      return true;
    }

    // Show options from current stage
    if (stageAssignment.taskStageId === currentStageId) {
      return true;
    }

    // Hide options from future stages
    return false;
  };

  const isOptionRequired = (optionType: string) =>
    !!onboardingOptionStages?.vm.onboardingOptionStages.find((stage) => stage.optionType === optionType)?.isRequired;

  const showRoleOption = (applicationApplyRoles && shouldShowOnboardingOption('role_applications')) || funnelHasRoles;
  const showActivityOption =
    (applicationActivityEnrolment && shouldShowOnboardingOption('activity_preferences')) || funnelHasActivities;
  const showAvailabilityOption = applicationAvailabilitySimple && shouldShowOnboardingOption('availability_simple');

  const additionalRoleOption = showRoleOption && !isOptionRequired('role_applications');
  const additionalActivityOption = showActivityOption && !isOptionRequired('activity_preferences');
  const additionalAvailabilityOption = showAvailabilityOption && !isOptionRequired('availability_simple');

  const profileId = useMemo(() => {
    return allApplicantTasksQuery.data?.vm.profile?.profileId;
  }, [allApplicantTasksQuery.data]);

  // Find the first unapproved blocking task
  const blockingTaskInfo = useMemo(() => {
    if (!allApplicantTasksQuery.data) return null;

    const { application } = allApplicantTasksQuery.data.vm;
    const completedTasks = allApplicantTasksQuery.data?.vm.profile?.completedTasks || [];
    const currentStageId = application?.currentStageId;
    const completedStageIds = application?.completedStageIds || [];

    // Only consider tasks the applicant can actually see (current / completed stage),
    // so a blocking task parked in a future stage doesn't hide current-stage tasks.
    const allRequiredTasks =
      application?.relevantTasks
        .filter((task) => task.cake.cakeType === 'required' && !task.dateRemoved)
        .filter((task) => {
          if (!task.stage) return true;
          if (!currentStageId) return !task.stage;
          if (completedStageIds.includes(task.stage.taskStageId)) return true;
          return task.stage.taskStageId === currentStageId;
        })
        .sort((a, b) => (a.order > b.order ? 1 : -1)) ?? [];

    // Find first blocking task that is NOT approved
    const blockingTask = allRequiredTasks.find((task) => {
      if (!task.blocksProgress) return false;

      const completedTask = completedTasks.find((ct) => ct.task.taskId === task.taskId && ct.dateRevoked === null);

      // Task is blocking if not completed, or completed but not approved
      return !completedTask || !isTaskApproved(completedTask.status);
    });

    if (!blockingTask) return null;

    // Check if the blocking task has been submitted
    const blockingCompletedTask = completedTasks.find(
      (ct) => ct.task.taskId === blockingTask.taskId && ct.dateRevoked === null
    );
    const isSubmitted = !!blockingCompletedTask;

    return { taskId: blockingTask.taskId, order: blockingTask.order, isSubmitted };
  }, [allApplicantTasksQuery.data]);

  const requiredTasks: Array<ApplicantTaskListItem> = useMemo(() => {
    if (!allApplicantTasksQuery.data) return [];

    const { application } = allApplicantTasksQuery.data.vm;
    const currentStageId = application?.currentStageId;
    const completedStageIds = application?.completedStageIds || [];

    const regularTasks =
      application?.relevantTasks
        // tasks from cakeType required
        .filter((task) => task.cake.cakeType === 'required' && !task.dateRemoved)
        // Hide completed admin-only tasks from applicants (they can't see responses anyway)
        .filter((task) => {
          if (!task.adminOnly) return true;

          // For referee check and event tasks, always show them regardless of completion status
          if (task.isRefereeCheckTask || task.isEventTask) return true;

          // For other admin-only tasks, only show if not completed
          const isCompleted = allApplicantTasksQuery.data?.vm.profile?.completedTasks.some(
            (completedTask) => completedTask.task.taskId === task.taskId && completedTask.dateRevoked === null
          );
          return !isCompleted;
        })
        // Filter based on stage progression
        .filter((task) => {
          // If no stage system in use (legacy), show all tasks
          if (!task.stage) {
            return true;
          }

          // If no current stage set, only show unstaged tasks
          if (!currentStageId) {
            return !task.stage;
          }

          // Show tasks from completed stages
          if (completedStageIds.includes(task.stage.taskStageId)) {
            return true;
          }

          // Show tasks from current stage
          if (task.stage.taskStageId === currentStageId) {
            return true;
          }

          // Hide tasks from future stages
          return false;
        })
        // Filter out tasks after an unapproved blocking task
        .filter((task) => {
          if (!blockingTaskInfo) return true;
          // Show tasks up to and including the blocking task, hide tasks after it
          return task.order <= blockingTaskInfo.order;
        })
        .sort((a, b) => (a.order > b.order ? 1 : -1))
        .map((task) => {
          const maybeCompletedTask = allApplicantTasksQuery.data?.vm.profile?.completedTasks.find(
            (completedTask) => completedTask.task.taskId === task.taskId && completedTask.dateRevoked === null
          );
          return {
            task,
            completedDate: maybeCompletedTask?.dateLastUpdated,
            status: maybeCompletedTask?.status,
            refereeContacts: maybeCompletedTask?.refereeContacts,
          };
        }) ?? [];

    const { profile } = allApplicantTasksQuery.data.vm;
    const requiredOptions = [
      {
        optionType: 'role_applications',
        taskId: 'onboarding-role-applications',
        visible: showRoleOption,
        defaultTitle: 'Roles',
        defaultDescription: 'Apply for roles and complete any required additional information',
        complete: (application?.applicationRoles ?? []).some((role) => role.status !== 'rejected'),
      },
      {
        optionType: 'activity_preferences',
        taskId: 'onboarding-activity-preferences',
        visible: showActivityOption,
        defaultTitle: pluralize(activityLabel),
        defaultDescription: `Indicate interest and apply for ${pluralize(
          activityLabel
        ).toLowerCase()} in your ${programLabel.toLowerCase()}`,
        complete: (application?.applicationActivities ?? []).length > 0,
      },
      {
        optionType: 'availability_simple',
        taskId: 'onboarding-availability-simple',
        visible: showAvailabilityOption,
        defaultTitle: 'Availability',
        defaultDescription: 'Indicate your general availability',
        complete: !!profile?.simpleAvailability,
      },
    ].filter((option) => option.visible && isOptionRequired(option.optionType));

    return requiredOptions.reduce<ApplicantTaskListItem[]>((tasks, option) => {
      const stage = getStageAssignment(option.optionType);
      const lastStageTaskIndex = stage
        ? tasks.map((item) => item.task.stage?.taskStageId).lastIndexOf(stage.taskStageId)
        : -1;
      const insertAt = lastStageTaskIndex >= 0 ? lastStageTaskIndex + 1 : tasks.length;

      if (blockingTaskInfo && insertAt === tasks.length) return tasks;

      const content = getOptionContent(option.optionType);
      const optionTask: ApplicantTaskListItem = {
        task: {
          __typename: 'VOLUNTEER_ApplicantTaskType',
          taskId: option.taskId,
          title: content.title || option.defaultTitle,
          description: content.description || option.defaultDescription,
          order: tasks[insertAt - 1]?.task.order ?? 0,
          adminOnly: false,
          isRefereeCheckTask: false,
          isEventTask: false,
          blocksProgress: false,
          cake: tasks[0]?.task.cake ?? { __typename: 'VOLUNTEER_CakeType', description: '', cakeType: 'required' },
          stage: null,
          automateApproval: true,
          dateRemoved: null,
        },
        completedDate: option.complete ? 'complete' : undefined,
        status: option.complete ? 'approved' : undefined,
      };

      return [...tasks.slice(0, insertAt), optionTask, ...tasks.slice(insertAt)];
    }, regularTasks);
  }, [
    allApplicantTasksQuery.data,
    blockingTaskInfo,
    onboardingOptionStages,
    showRoleOption,
    showActivityOption,
    showAvailabilityOption,
    activityLabel,
    programLabel,
  ]);

  const requiredCake = useMemo(() => {
    return arrayHead(
      (allApplicantTasksQuery?.data?.vm.application?.relevantTasks ?? []).filter(
        (task) => task.cake.cakeType === 'required'
      ) ?? []
    )?.cake;
  }, [allApplicantTasksQuery.data]);

  useMemo(() => {
    if (requiredTasks.length === 0) setProgress(0);
    if (
      requiredTasks.length ===
      requiredTasks.filter((task) => !!task.completedDate && isTaskApproved(task.status)).length
    ) {
      setProgress(100);
    }
    const numberCompletedTasks = requiredTasks.reduce(
      (previous, current) => previous + (current.completedDate ? 1 : 0),
      0
    );
    return setProgress(Math.round((numberCompletedTasks / requiredTasks.length) * 100));
  }, [requiredTasks]);

  const requiredTasksComplete = progress === 100;

  const tasksRequiringApproval = useMemo(() => {
    return requiredTasks.filter((task) => task.status === 'pending' || task.status === 'rejected');
  }, [requiredTasks]);

  const hasFutureStageTasks = useMemo(() => {
    const application = allApplicantTasksQuery.data?.vm.application;
    if (!application) return false;

    const { currentStageId } = application;
    const completedStageIds = application.completedStageIds || [];
    const relevantTasks = application.relevantTasks || [];

    return relevantTasks.some((task) => {
      if (!task.stage || task.dateRemoved) return false;
      if (task.stage.taskStageId === currentStageId) return false;
      if (completedStageIds.includes(task.stage.taskStageId)) return false;
      return true;
    });
  }, [allApplicantTasksQuery.data]);

  /**
   * Bunch of functionality to handle retry refresh token.
   * We spin this up if the volunteer profile successfully submits, because we're expecting there to be a resulting
   * volunteer entity within some reasonable time period.
   */
  const [refreshTokenTrys, setRefreshTokenTrys] = useState<number | undefined>();

  useEffect(() => {
    const isActive = refreshTokenTrys !== undefined && refreshTokenTrys < CONTRACT_SUBMISSION_TOKEN_RETRY_LIMIT;
    let interval: any;
    if (isActive) {
      interval = setInterval(() => {
        reloadSession();
        setRefreshTokenTrys((refreshTokenTrysPrev) => (refreshTokenTrysPrev ? refreshTokenTrysPrev + 1 : 1));
      }, CONTRACT_SUBMISSION_TOKEN_REFRESH_INTERVAL);
    } else if (!isActive && refreshTokenTrys !== undefined && interval) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [refreshTokenTrys]);

  const taskListItemClasses = useTaskListItemClasses();

  const handleSubmit = () => {
    if (!profileId) {
      return;
    }
    completeVolunteerProfile.run({ profileId }).then((res) => {
      if (!res.ok) {
        setSnackbar({
          open: true,
          message: 'Unable to submit volunteer profile',
          variant: 'error',
        });
        return;
      }

      setSnackbar({
        open: true,
        message: 'Volunteer profile submitted',
        variant: 'success',
      });
      reloadSession();
      setRefreshTokenTrys(1);
    });
  };

  return (
    <Box
      overflow='hidden auto'
      bgcolor='#f1f1f1'
      display='flex'
      flex='1 1 auto'
      flexDirection='column'
      width={1}
      paddingTop={2}
      paddingBottom={8}
      paddingX={1}
      boxSizing='border-box'
      style={{
        overflowX: 'hidden',
        overflowY: 'auto',
      }}
    >
      <CircularProgressOverlay
        isLoading={
          interactionsLoading ||
          completeVolunteerProfile.isLoading ||
          (refreshTokenTrys !== undefined &&
            refreshTokenTrys > 0 &&
            refreshTokenTrys < CONTRACT_SUBMISSION_TOKEN_RETRY_LIMIT)
        }
      />

      <Box>
        {requiredTasksComplete && hasFutureStageTasks ? (
          <Box width={1} display='flex' justifyContent='center'>
            <Box width={1} maxWidth={720} marginBottom={3}>
              <Cake>
                <CakeActions>
                  <VolunteerProfileAwaitingReviewMessage />
                </CakeActions>
              </Cake>
            </Box>
          </Box>
        ) : tasksRequiringApproval.length > 0 && requiredTasksComplete ? (
          <Box width={1} display='flex' justifyContent='center'>
            <Box width={1} maxWidth={720} marginBottom={3}>
              <Cake>
                <CakeActions>
                  <VolunteerProfileAwaitingReviewMessage />
                </CakeActions>
              </Cake>
            </Box>
          </Box>
        ) : requiredTasksComplete && applicationAutoVerification ? (
          <Box width={1} display='flex' justifyContent='center'>
            <Box width={1} maxWidth={720} marginBottom={3}>
              <Cake>
                <CakeActions>
                  <VolunteerProfileSubmitYourProfileMessage handleSubmit={handleSubmit} />
                </CakeActions>
              </Cake>
            </Box>
          </Box>
        ) : requiredTasksComplete && !applicationAutoVerification ? (
          <Box width={1} display='flex' justifyContent='center'>
            <Box width={1} maxWidth={720} marginBottom={3}>
              <Cake>
                <CakeActions>
                  <VolunteerProfileAwaitingReviewMessage />
                </CakeActions>
              </Cake>
            </Box>
          </Box>
        ) : (
          ''
        )}

        <Box width={1} display='flex' justifyContent='center'>
          <Box width={1} maxWidth={720} marginBottom={3}>
            <Cake>
              <Box style={{ padding: '20px 30px 10px 30px' }}>
                <Typography
                  style={{
                    fontWeight: 600,
                    fontSize: '19px',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#444',
                  }}
                >
                  Required Tasks
                </Typography>
                <Typography variant={'body2'} style={{ fontSize: '13px', color: '#808080' }}>
                  {requiredCake ? requiredCake.description : ''}
                </Typography>
              </Box>
              <CakeContent style={{ padding: '8px 0px 0px 0px' }}>
                <List style={{ paddingBottom: '0' }}>
                  {requiredTasks.map((task) => (
                    <TaskListItem task={task} key={task.task.taskId} />
                  ))}
                </List>
                {blockingTaskInfo?.isSubmitted && (
                  <Box
                    paddingY={2}
                    paddingX={3}
                    textAlign='center'
                    style={{ backgroundColor: '#f9f9f9', borderTop: '1px solid #eee' }}
                  >
                    <Typography variant='body2' color='textSecondary'>
                      <AccessTime style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 6 }} />
                      Pending approval from a manager. We&apos;ll email you when more tasks are available.
                    </Typography>
                  </Box>
                )}
              </CakeContent>
            </Cake>
          </Box>
        </Box>
        {additionalRoleOption || additionalActivityOption || additionalAvailabilityOption ? (
          <Box width={1} display='flex' justifyContent='center'>
            <Box width={1} maxWidth={720} marginBottom={3}>
              <Cake>
                <Box style={{ padding: '20px 30px 10px 30px' }}>
                  <Typography
                    style={{
                      fontWeight: 600,
                      fontSize: '19px',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#444',
                    }}
                  >
                    Additional
                  </Typography>
                  <Typography variant={'body2'} style={{ fontSize: '13px', color: '#808080' }}>
                    Complete the below items to support your application and suitability
                  </Typography>
                </Box>
                <CakeContent style={{ padding: '8px 0px 0px 0px' }}>
                  <List style={{ paddingBottom: '0' }}>
                    {additionalRoleOption && (
                      <Link
                        to={{
                          pathname: `/roles`,
                        }}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        <ListItem className={taskListItemClasses.listItem}>
                          <ListItemIcon style={{ paddingLeft: '15px' }}>
                            <EmojiPeople style={{ fontSize: '25px' }} />
                          </ListItemIcon>
                          <ListItemText>
                            <Typography
                              style={{
                                fontWeight: 600,
                                fontSize: '15px',
                                display: 'flex',
                                alignItems: 'center',
                                color: '#444',
                              }}
                            >
                              {getOptionContent('role_applications').title || 'Roles'}
                            </Typography>
                            <Typography variant={'body2'} style={{ fontSize: '13px', color: '#808080' }}>
                              {getOptionContent('role_applications').description ||
                                'Apply for roles and complete any required additional information'}
                            </Typography>
                          </ListItemText>
                          <ListItemIcon style={{ paddingLeft: '15px' }}>
                            <ChevronRight style={{ fontSize: 24, color: '#777' }} />
                          </ListItemIcon>
                        </ListItem>
                      </Link>
                    )}
                    {additionalActivityOption && (
                      <Link
                        to={{
                          pathname: `/activities`,
                        }}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        <ListItem className={taskListItemClasses.listItem}>
                          <ListItemIcon style={{ paddingLeft: '15px' }}>
                            <Event style={{ fontSize: '25px' }} />
                          </ListItemIcon>
                          <ListItemText>
                            <Typography
                              style={{
                                fontWeight: 600,
                                fontSize: '15px',
                                display: 'flex',
                                alignItems: 'center',
                                color: '#444',
                              }}
                            >
                              {getOptionContent('activity_preferences').title || pluralize(activityLabel)}
                            </Typography>
                            <Typography variant={'body2'} style={{ fontSize: '13px', color: '#808080' }}>
                              {getOptionContent('activity_preferences').description ||
                                `Indicate interest and apply for ${pluralize(
                                  activityLabel
                                ).toLowerCase()} in your ${programLabel.toLowerCase()}`}
                            </Typography>
                          </ListItemText>
                          <ListItemIcon style={{ paddingLeft: '15px' }}>
                            <ChevronRight style={{ fontSize: 24, color: '#777' }} />
                          </ListItemIcon>
                        </ListItem>
                      </Link>
                    )}
                    {additionalAvailabilityOption && (
                      <Link
                        to={{
                          pathname: `/availability`,
                        }}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        <ListItem className={taskListItemClasses.listItem}>
                          <ListItemIcon style={{ paddingLeft: '15px' }}>
                            <EventAvailable style={{ fontSize: '25px' }} />
                          </ListItemIcon>
                          <ListItemText>
                            <Typography
                              style={{
                                fontWeight: 600,
                                fontSize: '15px',
                                display: 'flex',
                                alignItems: 'center',
                                color: '#444',
                              }}
                            >
                              {getOptionContent('availability_simple').title || 'Availability'}
                            </Typography>
                            <Typography variant={'body2'} style={{ fontSize: '13px', color: '#808080' }}>
                              {getOptionContent('availability_simple').description ||
                                'Indicate your general availability'}
                            </Typography>
                          </ListItemText>
                          <ListItemIcon style={{ paddingLeft: '15px' }}>
                            <ChevronRight style={{ fontSize: 24, color: '#777' }} />
                          </ListItemIcon>
                        </ListItem>
                      </Link>
                    )}
                  </List>
                </CakeContent>
              </Cake>
            </Box>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

interface TaskListItemProps {
  task: ApplicantTaskListItem;
}

const TaskListItem = ({ task }: TaskListItemProps) => {
  const taskListItemClasses = useTaskListItemClasses();
  const location = useLocation();

  // Determine the correct link path for pseudo-tasks
  const getLinkPath = () => {
    switch (task.task.taskId) {
      case 'onboarding-activity-preferences':
        return '/activities';
      case 'onboarding-role-applications':
        return '/roles';
      case 'onboarding-availability-simple':
        return '/availability';
      default:
        return `/task/${task.task.taskId}`;
    }
  };

  return (
    <Link
      to={{
        pathname: getLinkPath(),
        search: task.completedDate ? `${location.search}` : location.search,
      }}
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <ListItem className={taskListItemClasses.listItem}>
        <ListItemIcon style={{ paddingLeft: '15px' }}>
          {task.task.isRefereeCheckTask &&
          task.status === 'pending' &&
          task.completedDate &&
          task.refereeContacts &&
          task.refereeContacts.some((contact) => contact.dateInvited) ? (
            <AccessTime style={{ color: '#ff9800', fontSize: 24 }} />
          ) : task.task.isEventTask && task.status === 'pending' && task.completedDate ? (
            <AccessTime style={{ color: '#ff9800', fontSize: 24 }} />
          ) : (
            <TaskCompleteIcon
              isComplete={
                task.task.isRefereeCheckTask
                  ? isTaskApproved(task.status)
                  : task.task.isEventTask
                  ? isTaskApproved(task.status)
                  : !!task.completedDate
              }
            />
          )}
        </ListItemIcon>
        <ListItemText>
          <Typography
            style={{ fontWeight: 600, fontSize: '15px', display: 'flex', alignItems: 'center', color: '#444' }}
          >
            {task.task.title}
          </Typography>
          <Typography variant={'body2'} style={{ fontSize: '13px', color: '#808080' }}>
            {task.task.description}
          </Typography>
        </ListItemText>
        <ListItemIcon style={{ paddingLeft: '15px' }}>
          <ChevronRight style={{ fontSize: 24, color: '#777' }} />
        </ListItemIcon>
      </ListItem>
    </Link>
  );
};

const useTaskListItemClasses = makeStyles((theme: Theme) =>
  createStyles({
    listItem: {
      '&:hover': {
        cursor: 'pointer',
        backgroundColor: theme.palette.grey[100],
      },
      border: '.6px solid #00000010',
      padding: '15px',
    },
    checkIcon: {
      fontSize: 29,
      color: '#00ed7e',
    },
    circleIcon: {
      fontSize: 29,
      color: theme.palette.grey[200],
    },
  })
);

const VolunteerProfileAwaitingReviewMessage = () => (
  <Box paddingRight={1} paddingBottom={1} width={1} display='flex' justifyContent='center'>
    <Box textAlign='center' paddingY={3}>
      <Typography variant='h5' style={{ fontWeight: 'bold' }} gutterBottom>
        <span role='img' aria-label='yay' style={{ marginRight: 8 }}>
          ⏳
        </span>
        Awaiting review
      </Typography>
      <Typography
        variant='body2'
        color='textSecondary'
      >{`We'll email you as soon as somebody has reviewed your tasks.`}</Typography>
    </Box>
  </Box>
);

const VolunteerProfileSubmitYourProfileMessage = ({ handleSubmit }: { handleSubmit: () => void }) => (
  <Box width={1} paddingY={2} textAlign='center' display='flex' justifyContent='center'>
    <Box>
      <Box textAlign='center' paddingBottom={1}>
        <Typography variant='h5' style={{ fontWeight: 'bold' }} gutterBottom>
          All tasks complete
        </Typography>
        <Typography variant='body2' color='textSecondary'>{`Ready to activate your volunteer profile?`}</Typography>
      </Box>
      <TabletButton
        variant='contained'
        color='primary'
        style={{ margin: 8 }}
        size='large'
        onClick={handleSubmit}
        endIcon={<SendRounded />}
      >
        {'Submit your profile'}
      </TabletButton>
    </Box>
  </Box>
);

export { SUYPOverview };
